import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, writeBatch } from 'firebase/firestore';
import { db } from '../../../firebase/firebase';
import 'react-quill/dist/quill.snow.css';
import LecturaForm from './LecturaForm';
import LecturasTable from './LecturasTable';
import EditLecturaModal from './EditLecturaModal';
import { Container, Toast, ToastContainer, Tabs, Tab, Alert } from 'react-bootstrap';
import NavbarGlobal from '../../NavbarGlobal';
import Swal from 'sweetalert2';
import '../../../theme/lecturas-alumno.scss';
import '../../../theme/letter-format.scss';
import { arrayMove } from '@dnd-kit/sortable';

const AddLecturasAdicionales = () => {
    const [lecturas, setLecturas] = useState([]);
    const [lecturaActual, setLecturaActual] = useState({ 
        level: '', 
        nombre: '', 
        descripcion: '', 
        columnas: '1',
        evaluations: [],
        freeResponseEvaluation: { title: '', description: '' }
    });
    const [editingLectura, setEditingLectura] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [mainTab, setMainTab] = useState('formulario');
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    const ORDER_STORAGE_KEY = 'ordenLecturasAdicionales';

    const normalizeLectura = (raw) => ({
        ...raw,
        level: raw?.level != null ? String(raw.level) : '',
        columnas: raw?.columnas || '1',
        evaluations: raw?.evaluations || [],
        freeResponseEvaluation: raw?.freeResponseEvaluation || { title: '', description: '' },
        posicion: raw?.posicion ?? raw?.orden ?? undefined,
    });

    const sortLecturas = (items) => {
        const storedRaw = localStorage.getItem(ORDER_STORAGE_KEY);
        let stored = null;
        if (storedRaw) {
            try {
                stored = JSON.parse(storedRaw);
            } catch (e) {
                stored = null;
            }
        }

        const niveles = ['1', '2', '3'];
        const result = [];

        for (const nivel of niveles) {
            const group = items.filter((l) => String(l.level) === nivel);
            const ids = stored && stored[nivel] ? stored[nivel] : null;

            if (Array.isArray(ids) && ids.length > 0) {
                const orderMap = new Map(ids.map((id, index) => [id, index]));
                group.sort((a, b) => {
                    const aIndex = orderMap.has(a.id) ? orderMap.get(a.id) : Number.MAX_SAFE_INTEGER;
                    const bIndex = orderMap.has(b.id) ? orderMap.get(b.id) : Number.MAX_SAFE_INTEGER;
                    return aIndex - bIndex;
                });
            } else {
                group.sort(
                    (a, b) =>
                        (a.posicion || Number.MAX_SAFE_INTEGER) - (b.posicion || Number.MAX_SAFE_INTEGER)
                );
            }

            result.push(...group);
        }

        const leftovers = items.filter((l) => !niveles.includes(String(l.level)));
        leftovers.sort(
            (a, b) => (a.posicion || Number.MAX_SAFE_INTEGER) - (b.posicion || Number.MAX_SAFE_INTEGER)
        );
        result.push(...leftovers);

        return result;
    };

    useEffect(() => {
        const obtenerLecturas = async () => {
            try {
                setIsLoading(true);
                const querySnapshot = await getDocs(collection(db, 'lecturasAdicionales'));
                const loaded = querySnapshot.docs.map((d) => normalizeLectura({ id: d.id, ...d.data() }));
                setLecturas(sortLecturas(loaded));
            } catch (error) {
                console.error('Error al obtener las lecturas:', error);
                setToastMessage('Error al cargar las lecturas');
                setShowToast(true);
            } finally {
                setIsLoading(false);
            }
        };
        obtenerLecturas();
    }, []);

    const handleAddLectura = async (nuevaLectura) => {
        try {
            const normalized = normalizeLectura(nuevaLectura);
            const nivel = String(normalized.level || '');
            const maxPos = Math.max(
                0,
                ...lecturas
                    .filter((l) => String(l.level) === nivel)
                    .map((l) => (typeof l.posicion === 'number' ? l.posicion : Number(l.posicion) || 0))
            );
            const posicion = maxPos + 1;

            const docRef = await addDoc(collection(db, 'lecturasAdicionales'), { ...normalized, posicion });
            const lecturaConId = { ...normalized, posicion, id: docRef.id };
            await updateDoc(docRef, lecturaConId);
            setLecturas((prev) => sortLecturas([...prev, lecturaConId]));
            setLecturaActual({ 
                level: '', 
                nombre: '', 
                descripcion: '', 
                columnas: '1',
                evaluations: [],
                freeResponseEvaluation: { title: '', description: '' }
            });

            setToastMessage('Lectura guardada con éxito');
            setShowToast(true);
            setMainTab('listado');
        } catch (error) {
            console.error('Error al guardar la lectura:', error);
            setToastMessage('Error al guardar la lectura');
            setShowToast(true);
        }
    };

    const handleEditLectura = (lectura) => {
        if (!lectura.id) {
            console.error('Error: La lectura seleccionada no tiene un ID válido.');
            return;
        }

        const lecturaParaEditar = {
            ...lectura,
            evaluations: lectura.evaluations || [],
            freeResponseEvaluation: lectura.freeResponseEvaluation || { title: '', description: '' }
        };

        setEditingLectura(lecturaParaEditar);
        setShowModal(true);
    };

    const handleDeleteLectura = async (id) => {
        try {
            const result = await Swal.fire({
                title: '¿Estás seguro?',
                text: "Esta acción no se puede deshacer",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Sí, eliminar',
                cancelButtonText: 'Cancelar'
            });

            if (result.isConfirmed) {
                await deleteDoc(doc(db, 'lecturasAdicionales', id));
                setLecturas(lecturas.filter(lectura => lectura.id !== id));
                setToastMessage('Lectura eliminada con éxito');
                setShowToast(true);
            }
        } catch (error) {
            console.error('Error al eliminar la lectura:', error);
            setToastMessage('Error al eliminar la lectura');
            setShowToast(true);
        }
    };

    const handleUpdateLectura = async (updatedLectura) => {
        if (!updatedLectura || !updatedLectura.id) {
            console.error('Error: Lectura inválida o sin ID');
            return;
        }
    
        try {
            const lecturaRef = doc(db, 'lecturasAdicionales', updatedLectura.id);
    
            const lecturaData = {
                level: updatedLectura.level,
                nombre: updatedLectura.nombre,
                descripcion: updatedLectura.descripcion,
                columnas: updatedLectura.columnas,
                evaluations: updatedLectura.evaluations || [],
                freeResponseEvaluation: updatedLectura.freeResponseEvaluation || { title: '', description: '' }
            };
    
            await updateDoc(lecturaRef, lecturaData);
    
            setLecturas(prevLecturas =>
                prevLecturas.map(lectura =>
                    lectura.id === updatedLectura.id ? { ...lectura, ...lecturaData } : lectura
                )
            );
    
            setShowModal(false);
            setEditingLectura(null);
    
            Swal.fire({
                icon: 'success',
                title: 'Lectura actualizada con éxito',
                showConfirmButton: false,
                timer: 2000,
            });
        } catch (error) {
            console.error('Error al actualizar la lectura:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo actualizar la lectura. Inténtalo nuevamente.',
            });
        }
    };

    const debounce = (func, delay) => {
        let timer;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => func(...args), delay);
        };
    };

    const saveOrderInDatabase = debounce(async (nivelItems) => {
        try {
            const batch = writeBatch(db);
            nivelItems.forEach((item) => {
                batch.update(doc(db, 'lecturasAdicionales', item.id), { posicion: item.posicion });
            });
            await batch.commit();
            setToastMessage('Orden actualizado con éxito');
            setShowToast(true);
        } catch (error) {
            console.error('Error al actualizar posiciones en la base de datos:', error);
            setToastMessage('Error al actualizar el orden');
            setShowToast(true);
        }
    }, 1200);

    const handleOnDragEnd = (event, nivel) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const lecturasNivel = lecturas
            .filter((l) => String(l.level) === String(nivel))
            .sort((a, b) => (a.posicion || Number.MAX_SAFE_INTEGER) - (b.posicion || Number.MAX_SAFE_INTEGER));

        const oldIndex = lecturasNivel.findIndex((l) => l.id === active.id);
        const newIndex = lecturasNivel.findIndex((l) => l.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;

        const reorderedNivel = arrayMove(lecturasNivel, oldIndex, newIndex).map((item, index) => ({
            ...item,
            posicion: index + 1,
        }));

        const updated = lecturas.map((l) => {
            if (String(l.level) !== String(nivel)) return l;
            const replacement = reorderedNivel.find((r) => r.id === l.id);
            return replacement || l;
        });

        setLecturas(sortLecturas(updated));

        const storedRaw = localStorage.getItem(ORDER_STORAGE_KEY);
        let stored = {};
        if (storedRaw) {
            try {
                stored = JSON.parse(storedRaw) || {};
            } catch (e) {
                stored = {};
            }
        }
        stored[String(nivel)] = reorderedNivel.map((l) => l.id);
        localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(stored));

        saveOrderInDatabase(reorderedNivel);
    };

    return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <NavbarGlobal
                title="Lecturas Adicionales"
                showBackButton={true}
            />
            <div style={{ flex: 1, overflowY: 'auto' }}>
                <Container className="lecturas-container">
                    <div className="lecturas-header">
                        <h3>Gestión de Lecturas Adicionales</h3>
                        <p className="text-muted">Administra las lecturas adicionales para cada nivel</p>
                    </div>

                    {isLoading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Cargando...</span>
                            </div>
                            <p className="mt-3">Cargando lecturas...</p>
                        </div>
                    ) : (
                        <>
                            <Tabs 
                                activeKey={mainTab} 
                                onSelect={(k) => setMainTab(k)} 
                                className="mb-4"
                                justify
                            >
                                <Tab eventKey="formulario" title="Formulario">
                                    <div className="lecturas-form-container">
                                        <Alert variant="info" className="mb-4">
                                            <Alert.Heading>📝 Información</Alert.Heading>
                                            <p>
                                                Completa el formulario para añadir una nueva lectura. Asegúrate de seleccionar el nivel correcto y proporcionar una descripción detallada.
                                            </p>
                                        </Alert>
                                        <LecturaForm
                                            lectura={lecturaActual}
                                            setLectura={setLecturaActual}
                                            handleSubmit={handleAddLectura}
                                            buttonText="Añadir Lectura"
                                        />
                                    </div>
                                </Tab>
                                <Tab eventKey="listado" title="Listado de Lecturas">
                                    <div className="lecturas-table-container">
                                        <Alert variant="info" className="mb-4">
                                            <Alert.Heading>📚 Gestión de Lecturas</Alert.Heading>
                                            <p>
                                                Aquí puedes ver, editar y eliminar las lecturas existentes. También puedes reordenar las lecturas arrastrándolas.
                                            </p>
                                        </Alert>
                                        <LecturasTable
                                            lecturas={lecturas}
                                            handleEdit={handleEditLectura}
                                            handleDelete={handleDeleteLectura}
                                            handleOnDragEnd={handleOnDragEnd}
                                        />
                                    </div>
                                </Tab>
                            </Tabs>

                            <EditLecturaModal
                                show={showModal}
                                handleClose={() => setShowModal(false)}
                                lectura={editingLectura}
                                setLectura={setEditingLectura}
                                handleSave={handleUpdateLectura}
                            />

                            <ToastContainer position="bottom-end" className="p-3">
                                <Toast onClose={() => setShowToast(false)} show={showToast} delay={3000} autohide>
                                    <Toast.Header>
                                        <strong className="me-auto">Notificación</strong>
                                    </Toast.Header>
                                    <Toast.Body>{toastMessage}</Toast.Body>
                                </Toast>
                            </ToastContainer>
                        </>
                    )}
                </Container>
            </div>
        </div>
    );
};

export { AddLecturasAdicionales };
